import { useCallback } from "react";
import { useApi } from "../../../hooks/useApi";
import { crmQueryClient } from "../../../modules/crm/api/queryClient";

// Invalidate the CRM module's React Query caches so the CRM UI reflects changes
// made from TG (project list, members, project-members). crmQueryClient is a
// shared singleton, so this works even though we're outside the CRM provider.
// Wrapped in try/catch — purely a refresh nicety, never blocks the action.
function invalidateCrm() {
  try {
    crmQueryClient.invalidateQueries({ queryKey: ["crm", "projects"] });
    crmQueryClient.invalidateQueries({ queryKey: ["crm", "members"] });
    crmQueryClient.invalidateQueries({ queryKey: ["crm", "project-members"] });
  } catch (_) { /* no-op */ }
}

// Client for the additive /api/v1/crm-binding module.
// Per-entity connect/disconnect for CRM access, project links, project members,
// and a read-only unified Gmail status. Everything here is additive — calling
// these never affects users/projects the admin didn't explicitly act on.
export function useBinding() {
  const api = useApi();

  // ── CRM access ──
  const getCrmAccess = useCallback(async (userIds = []) => {
    const qs = userIds.length ? `?userIds=${userIds.join(",")}` : "";
    const res = await api.get(`/api/v1/crm-binding/crm-access${qs}`);
    return res.data?.data || {};
  }, [api]);

  const connectCrmAccess = useCallback(async (userId, role = "agent") => {
    const res = await api.post(`/api/v1/crm-binding/crm-access/${userId}`, { role });
    invalidateCrm();
    return res.data?.data;
  }, [api]);

  const disconnectCrmAccess = useCallback(async (userId) => {
    const res = await api.delete(`/api/v1/crm-binding/crm-access/${userId}`);
    invalidateCrm();
    return res.data?.data;
  }, [api]);

  // ── Gmail unified status (read-only) ──
  const getGmailStatus = useCallback(async (userIds = []) => {
    const qs = userIds.length ? `?userIds=${userIds.join(",")}` : "";
    const res = await api.get(`/api/v1/crm-binding/gmail-status${qs}`);
    return res.data?.data || {};
  }, [api]);

  // ── Project ↔ CRM link ──
  const getProjectLinks = useCallback(async (projectIds = []) => {
    const qs = projectIds.length ? `?projectIds=${projectIds.join(",")}` : "";
    const res = await api.get(`/api/v1/crm-binding/project-link${qs}`);
    return res.data?.data || {};
  }, [api]);

  const connectProject = useCallback(async (tgProjectId, { name, description } = {}) => {
    const res = await api.post(`/api/v1/crm-binding/project-link/${tgProjectId}`, { name, description });
    invalidateCrm();
    return res.data?.data;
  }, [api]);

  const disconnectProject = useCallback(async (tgProjectId) => {
    const res = await api.delete(`/api/v1/crm-binding/project-link/${tgProjectId}`);
    invalidateCrm();
    return res.data?.data;
  }, [api]);

  // ── Mirror project members into linked CRM project (per-project, explicit) ──
  const syncProjectMembers = useCallback(async (tgProjectId, userIds = []) => {
    try {
      const res = await api.put(`/api/v1/crm-binding/project-members/${tgProjectId}`, { userIds });
      invalidateCrm();
      return res.data?.data;
    } catch (_) { return { synced: false }; } // never block the TG save
  }, [api]);

  // ── Calls (Plivo numbers) ──
  const getOrgNumbers = useCallback(async () => {
    const res = await api.get(`/api/v1/crm-binding/call-numbers/org`);
    return res.data?.data?.numbers || [];
  }, [api]);

  const getProjectNumbers = useCallback(async (tgProjectId) => {
    const res = await api.get(`/api/v1/crm-binding/call-numbers/${tgProjectId}`);
    return res.data?.data || { numbers: [], linked: false };
  }, [api]);

  const setProjectNumbers = useCallback(async (tgProjectId, numbers) => {
    const res = await api.put(`/api/v1/crm-binding/call-numbers/${tgProjectId}`, { numbers });
    try { crmQueryClient.invalidateQueries({ queryKey: ["crm", "project-call-numbers"] }); } catch (_) {}
    return res.data?.data?.numbers || [];
  }, [api]);

  return {
    getCrmAccess, connectCrmAccess, disconnectCrmAccess,
    getGmailStatus,
    getProjectLinks, connectProject, disconnectProject, syncProjectMembers,
    getOrgNumbers, getProjectNumbers, setProjectNumbers,
  };
}
